import {Box} from "@mui/material";
import Typography from "@mui/material/Typography";
import type {TFunction} from "i18next";
import React from "react";
import {Parts, ServiceRow, SummaryRow, useRowTone} from "./SigningRows";
import {SigningFormat} from "./format.util";
import {DigitalSigningSnapshot, SnapshotLine} from "./digitalSigning.types";

interface Props {
    snapshot: DigitalSigningSnapshot;
    format: SigningFormat;
    t: TFunction;
}

const MINUS = '−';

const isSpa = (snapshot: DigitalSigningSnapshot) => snapshot.reservation?.sectorType === 'SPA';
const isRestaurant = (snapshot: DigitalSigningSnapshot) => snapshot.kind === 'RESTAURANT';
const isTreatmentLine = (line: SnapshotLine) => Array.isArray(line.staffNames);

export const signingTitle = (snapshot: DigitalSigningSnapshot, t: TFunction, language?: string): string => {
    const own = language ? snapshot.titleTexts?.[language]?.trim() : undefined;
    if (own) return own;
    if (isRestaurant(snapshot)) return t('Restaurant confirmation');
    return isSpa(snapshot) ? t('Spa confirmation') : t('Confirmation');
};

export const signingSubtitle = (snapshot: DigitalSigningSnapshot, language?: string): string | undefined => (
    (language ? snapshot.subtitleTexts?.[language]?.trim() : undefined) || undefined
);

const guestNameOf = ({guest}: DigitalSigningSnapshot) => (
    [guest?.firstName, guest?.lastName].map(part => part?.trim()).filter(Boolean).join(' ')
);

const pricingOf = (line: SnapshotLine, format: SigningFormat, t: TFunction): string | undefined => {
    const discounted = line.discountCents > 0;
    const parts = [
        line.unitPriceCents !== undefined && (line.quantity !== 1 || discounted) && `${line.quantity} × ${format.money(line.unitPriceCents)}`,
        discounted && `${t('Discount')} ${MINUS}${format.money(line.discountCents)}`,
    ].filter(Boolean);
    return parts.length ? parts.join(' · ') : undefined;
};

export function SigningAmountRow({snapshot, format, t}: Props) {
    const lines = snapshot.lines ?? [];
    const totals = snapshot.totals;
    const [firstLine] = lines;
    const single = lines.length <= 1;

    const vat = (totals.vatByRate ?? [])
        .filter(row => row.vatCents !== 0)
        .map(row => `${t('incl. {{rate}} VAT', {rate: format.percent(row.rate, 3)})} ${format.money(row.vatCents)}`);
    const pricing = single && firstLine ? pricingOf(firstLine, format, t) : undefined;
    const caption = [pricing, ...vat].filter(Boolean) as string[];

    return (
        <SummaryRow
            label={t('Amount')}
            value={single && firstLine?.included ? t('Included') : format.money(totals.grossCents)}
            caption={caption.length ? caption.join(' · ') : undefined}
            amount
        />
    );
}

export function SigningSummary({snapshot, format, t, withAmount}: Props & {withAmount?: boolean}) {
    const tone = useRowTone();
    const lines = snapshot.lines ?? [];
    const [firstLine] = lines;
    const single = lines.length <= 1;
    const restaurant = isRestaurant(snapshot);
    const packageName = lines.some(isTreatmentLine) ? snapshot.reservation?.serviceName?.trim() : undefined;

    const descriptionOf = (line?: SnapshotLine) => line?.description || snapshot.reservation?.serviceName || '';
    const durationOf = (line?: SnapshotLine) => {
        const minutes = line?.durationMinutes ?? (single ? snapshot.reservation?.durationMinutes : undefined);
        return minutes ? format.duration(minutes) : undefined;
    };

    const namesOf = (names?: string[]) => (names ?? []).map(name => name?.trim()).filter(Boolean).join(', ');
    const staff = namesOf(snapshot.reservation?.staffNames);
    const tables = (snapshot.reservation?.tableNames ?? []).map(name => name?.trim()).filter(Boolean);
    const guestCount = snapshot.reservation?.guestCount;
    const guest = guestNameOf(snapshot);
    const room = snapshot.guest?.roomNumber?.trim();
    const start = single ? (firstLine?.start ?? snapshot.reservation?.start) : (snapshot.reservation?.start ?? firstLine?.start);
    const day = format.day(start);

    const captionOf = (line: SnapshotLine) => {
        const lineDay = format.day(line.start);
        const treatment = isTreatmentLine(line)
            ? [lineDay !== day && lineDay, format.time(line.start), namesOf(line.staffNames)]
            : [];
        return [...treatment, pricingOf(line, format, t)].filter(Boolean).join(' · ') || undefined;
    };

    const services = (
        <Box sx={{py: 1.25}}>
            <Typography component={'dt'} variant={'body1'} sx={{color: tone.label}}>
                {t('Services', {context: 'signing'})}
            </Typography>
            <Box component={'dd'} sx={{m: 0, mt: 0.5}}>
                <Box component={'ul'} sx={{listStyle: 'none', m: 0, p: 0}}>
                    {lines.map((line, index) => (
                        <ServiceRow
                            key={index}
                            name={<Parts parts={[descriptionOf(line), durationOf(line)]}/>}
                            amount={line.included ? t('Included') : format.money(line.grossCents)}
                            caption={captionOf(line)}
                        />
                    ))}
                </Box>
            </Box>
        </Box>
    );

    return (
        <Box component={'dl'} sx={{m: 0, py: 1, borderTop: '1px solid', borderBottom: '1px solid', borderColor: tone.rule}}>
            {!!packageName && <SummaryRow label={t('Package')} value={packageName}/>}
            {restaurant ? (
                <>
                    {tables.length > 0 && <SummaryRow label={tables.length > 1 ? t('Tables') : t('Table')} value={tables.join(', ')}/>}
                    {!!guestCount && <SummaryRow label={t('Guests')} value={String(guestCount)}/>}
                    {!single && services}
                </>
            ) : (
                <>
                    {single ? (
                        (!!descriptionOf(firstLine) || !!durationOf(firstLine)) && (
                            <SummaryRow label={t('Treatment')} value={<Parts parts={[descriptionOf(firstLine), durationOf(firstLine)]}/>}/>
                        )
                    ) : services}
                    {!!staff && (
                        <SummaryRow label={isSpa(snapshot) ? t('Treated by') : t('Staff', {context: 'signing'})} value={staff}/>
                    )}
                </>
            )}
            {(!!guest || !!room) && (
                <SummaryRow label={t('Guest')} value={<Parts parts={[guest, !!room && t('Room {{room}}', {room})]}/>}/>
            )}
            {!!day && <SummaryRow label={t('Date')} value={<Parts parts={[day, format.time(start)]}/>}/>}
            {withAmount && <SigningAmountRow snapshot={snapshot} format={format} t={t}/>}
        </Box>
    );
}
